import React, { useState } from "react";
import { api } from "@/lib/apiClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, TrendingUp, FileText, Zap } from "lucide-react";
import MarkdownLite from "@/components/MarkdownLite";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which influencer has the best ROI this quarter?",
  "How many videos were published this month?",
  "Show me all PR coverage this period",
  "Where is the content pipeline stuck?",
];

export default function AiPanel({ open, setOpen }) {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const ask = async (q) => {
    const question = q || query;
    if (!question.trim()) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setQuery("");
    setLoading(true);
    try {
      const res = await api.post("/ai/query", { query: question });
      setMessages((m) => [...m, { role: "ai", text: res.data.answer }]);
    } catch (e) {
      toast.error("AI request failed");
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't process that." }]);
    } finally { setLoading(false); }
  };

  const runTool = async (path, label) => {
    setMessages((m) => [...m, { role: "user", text: label }]);
    setLoading(true);
    try {
      const res = await api.post(path);
      setMessages((m) => [...m, { role: "ai", text: res.data.answer || res.data.summary }]);
    } catch (e) {
      toast.error("AI request failed");
    } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-surface border-surface-hover text-white w-full sm:max-w-md flex flex-col p-0" data-testid="ai-panel">
        <SheetHeader className="p-5 border-b border-surface-hover">
          <SheetTitle className="text-white font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" /> AI Content Intelligence
          </SheetTitle>
        </SheetHeader>

        <div className="flex gap-2 p-4 border-b border-surface-hover flex-wrap">
          <button data-testid="ai-viral-btn" onClick={() => runTool("/ai/viral-patterns", "Detect viral patterns")} className="flex items-center gap-1.5 text-xs bg-navy border border-surface-hover rounded-md px-2.5 py-1.5 hover:border-gold transition-colors duration-200">
            <Zap className="w-3.5 h-3.5 text-gold" /> Viral patterns
          </button>
          <button data-testid="ai-summary-btn" onClick={() => runTool("/ai/monthly-summary", "Draft monthly summary")} className="flex items-center gap-1.5 text-xs bg-navy border border-surface-hover rounded-md px-2.5 py-1.5 hover:border-gold transition-colors duration-200">
            <FileText className="w-3.5 h-3.5 text-gold" /> Monthly summary
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-slatetext text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" /> Try asking:</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="ai-suggestion" onClick={() => ask(s)} className="block w-full text-left text-sm bg-navy border border-surface-hover rounded-md px-3 py-2.5 hover:border-gold transition-colors duration-200">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
              <div className={`inline-block max-w-[90%] rounded-lg px-3.5 py-2.5 text-left ${m.role === "user" ? "bg-gold text-navy" : "bg-navy border border-surface-hover"}`}>
                {m.role === "user" ? m.text : <MarkdownLite text={m.text} />}
              </div>
            </div>
          ))}
          {loading && <div className="text-slatetext text-sm animate-pulse">Analyzing data…</div>}
        </div>

        <div className="p-4 border-t border-surface-hover flex gap-2">
          <input
            data-testid="ai-query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="Ask about your data…"
            className="flex-1 bg-navy border border-surface-hover rounded-md px-3 py-2.5 text-sm text-white focus:border-gold outline-none"
          />
          <Button data-testid="ai-send-btn" onClick={() => ask()} disabled={loading} className="bg-gold text-navy hover:bg-gold-hover">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
